import { defineComponent } from '@digitalbranch/app'

import { uiFocusRing } from '../../focus-ring/focus-ring'

import template from './base-button.html'
import css from './base-button.css'

function hasNonEmptyChildNodes(slot: HTMLSlotElement) {
  return slot.assignedNodes({ flatten: true }).some(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent && node.textContent.trim().length > 0
    }
    return node.nodeType === Node.ELEMENT_NODE
  })
}

export const uiBaseButton = defineComponent({
  name: 'ui-base-button',
  props: ['type', { name: 'disabled', type: 'boolean' }, 'to', 'href'],
  components: {
    uiFocusRing,
  },
  template,
  css,
  data() {
    return {
      hasIconStart: false,
      hasIconEnd: false,
      hasLabel: false,
      showFocusRing: true,
      hasFocus: false,
    }
  },
  formAssociated: true,
  delegatesFocus: true,
  listeners: {
    ready() {
      const { $node } = this
      const parent = $node.parentNode
      const host = (parent instanceof ShadowRoot ? parent.host : parent) as HTMLElement | null

      $node.setAttribute('tabindex', '0')
      $node.setAttribute('role', this.to || this.href ? 'link' : 'button')

      this.$addEventListener($node, 'focus', this.handleFocus.bind(this))
      this.$addEventListener($node, 'focusout', this.handleFocusOut.bind(this))
      this.$addEventListener($node, 'mousedown', this.handleMouseDown.bind(this))
      this.$addEventListener($node, 'keydown', this.handleKeyDown.bind(this))

      if (host) {
        host.setAttribute('tabindex', '0')

        this.$addEventListener(host, 'focusout', this.handleFocusOutParent.bind(this))
        this.$addEventListener(host, 'keydown', this.keyDownParent.bind(this))
      }
    },
    beforeDestroy() {
      const { $node } = this

      this.$removeEventListener($node, 'focus', this.handleFocus.bind(this))
      this.$removeEventListener($node, 'focusout', this.handleFocusOut.bind(this))
      this.$removeEventListener($node, 'mousedown', this.handleMouseDown.bind(this))
      this.$removeEventListener($node, 'keydown', this.handleKeyDown.bind(this))
    },
  },
  methods: {
    keyDownParent(e: KeyboardEvent) {
      const { key, shiftKey } = e
      const target = e.currentTarget as HTMLElement

      if (key === 'Tab' && shiftKey) {
        target.setAttribute('tabindex', '-1')
      }
    },

    handleFocusOutParent(e: FocusEvent) {
      const target = e.currentTarget as HTMLElement

      target.setAttribute('tabindex', '0')
    },
    handleSlotChange(e: Event) {
      const target = e.target as HTMLSlotElement

      if (!target) return

      const { name, assignedSlot } = target
      const hasNodes = hasNonEmptyChildNodes(target)

      if (name === 'icon-start' || assignedSlot?.name === 'icon-start') {
        this.hasIconStart = hasNodes
      } else if (name === 'icon-end') {
        this.hasIconEnd = hasNodes
      } else if (name === '') {
        this.hasLabel = hasNodes
      }
    },
    handleKeyDown(e: KeyboardEvent) {
      const { key } = e

      if (key === 'Enter' || key === ' ') {
        const ripple = this.$node.querySelector('ui-ripple') as HTMLElement | null

        if (ripple) {
          ripple.dispatchEvent(new MouseEvent('mousedown', { button: 0 }))
          window.dispatchEvent(new MouseEvent('mouseup'))
        }
      }
    },
    handleMouseDown() {
      this.showFocusRing = false
    },
    handleFocus() {
      this.hasFocus = true
    },
    handleFocusOut() {
      this.hasFocus = false
      this.showFocusRing = true
    },
    handleClick() {
      if (this.type === 'submit') {
        const button = this.$node as HTMLButtonElement

        if (button.form) button.form.dispatchEvent(new Event('submit'))
      }
    },
  },
  computed: {
    buttonClasses() {
      return [
        this.hasLabel && 'ui-button--has-label',
        this.hasIconStart && 'ui-button--has-icon-start',
        this.hasIconEnd && 'ui-button--has-icon-end',
      ]
    },
  },
})
